import type { ReactNode } from 'react';
import {
  Body1,
  Caption1,
  Subtitle2,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';

/**
 * The mobile face of every data table in the app (#53, wireframes S1a/S1b/S1c).
 *
 * Teams forbids horizontal scrolling of the tab canvas, so below
 * `MOBILE_BREAKPOINT_PX` a table is replaced — not restyled — by one card per
 * row, with **every** field present and labelled. Each page renders this
 * instead of its `Table` when `useIsNarrowViewport()` is true, and reuses the
 * exact column headings as card labels so the two presentations describe the
 * same data in the same words.
 *
 * Semantically a list, not a table: a two-cell "row" of label and value in a
 * one-column layout is a list item, and screen readers announce the label with
 * its value rather than replaying a column header from far off-screen.
 */

export interface DataCardField {
  /** Reuse the desktop column heading verbatim — never invent mobile-only copy. */
  label: string;
  value: ReactNode;
}

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    listStyleType: 'none',
    padding: '0',
    margin: '0',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  fields: { display: 'flex', flexDirection: 'column', gap: '4px' },
  field: { display: 'flex', justifyContent: 'space-between', gap: '12px' },
  label: { color: tokens.colorNeutralForeground2, flexShrink: '0' },
  // Wraps rather than clips: a long item description must never widen the card
  // past the canvas, which is the whole failure mode this replaces.
  value: { fontWeight: tokens.fontWeightSemibold, textAlign: 'right', overflowWrap: 'anywhere' },
  actions: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    paddingTop: '8px',
    borderTop: `2px solid ${tokens.colorNeutralStroke1}`,
    fontWeight: tokens.fontWeightSemibold,
  },
});

export function DataCardList({ ariaLabel, children }: { ariaLabel: string; children: ReactNode }) {
  const styles = useStyles();
  return (
    <ul aria-label={ariaLabel} className={styles.list}>
      {children}
    </ul>
  );
}

export function DataCard({
  heading,
  fields,
  actions,
}: {
  heading?: ReactNode;
  fields: DataCardField[];
  actions?: ReactNode;
}) {
  const styles = useStyles();
  const responsive = useResponsiveStyles();
  return (
    <li className={styles.card}>
      {heading !== undefined && <Subtitle2>{heading}</Subtitle2>}
      <div className={styles.fields}>
        {fields.map((field) => (
          <div key={field.label} className={styles.field}>
            <Caption1 className={styles.label}>{field.label}</Caption1>
            <Body1 as="span" className={styles.value}>
              {field.value}
            </Body1>
          </div>
        ))}
      </div>
      {actions && (
        <div className={mergeClasses(styles.actions, responsive.touchTargets)}>{actions}</div>
      )}
    </li>
  );
}

/**
 * The card-layout equivalent of a table's `<tfoot>` — the grand total, which
 * belongs to the whole list rather than to any one card.
 */
export function DataCardTotal({ label, value }: { label: string; value: ReactNode }) {
  const styles = useStyles();
  return (
    <div className={styles.footer}>
      <Body1 as="span">{label}</Body1>
      <Body1 as="span">{value}</Body1>
    </div>
  );
}
